import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Add, CardGiftcard, DeleteOutline, Remove, Undo } from "@mui/icons-material";
import { Order, Product } from "../../../types";
import { ProductImage } from "../../../types/productImage";
import { orderService } from "../../../services/orderService";
import { stockService } from "../../../services/stockService";
import { formatCurrencyBDT } from "../../../utils/currency";
import { unitPriceFor } from "../../../utils/productPricing";
import { ComboPriceHint } from "../ComboPriceHint";
import { ProductSearchAutocomplete } from "../../common/ProductSearchAutocomplete";
import { ProductThumbnail } from "../../common/ProductThumbnail";
import { ChangeRow, ConfirmChangeDialog } from "./ConfirmChangeDialog";
import { balanceText, codWarning, lineTotal, MoneyImpact, orderTotalFor, round2 } from "./orderMath";
import { errorText, Notify, useQuickEditSave } from "./useQuickEditSave";

interface OrderItemsEditorProps {
    order: Order;
    /** Net amount paid so far. */
    paid: number;
    /** Leave edit mode (after saving or cancelling). */
    onDone: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

interface Line {
    key: string;
    /** Set for lines already on the order. */
    itemId?: string;
    productId: string;
    name: string;
    images?: ProductImage[] | null;
    isGift: boolean;
    unitPrice: number;
    /** 0 while the quantity box is empty — invalid until filled in. */
    quantity: number;
    originalQuantity?: number;
    removed: boolean;
    /** New paid lines only: their price follows the product's combo prices
     * as the quantity changes. Lines already on the order keep theirs. */
    product?: Product;
}

interface Step {
    label: string;
    run: (allowNegativeStock: boolean) => Promise<unknown>;
}

/** What the confirmation shows and saves — frozen when Review is pressed. */
interface Review {
    rows: ChangeRow[];
    impact: MoneyImpact;
    warnings: string[];
    stockIssues: string[];
    steps: Step[];
}

const MAX_QTY = 9999;
const LAST_ITEM = "An order needs at least one item — cancel the order instead.";

const linesFrom = (order: Order): Line[] =>
    (order.items ?? []).map((item) => ({
        key: item.item_id,
        itemId: item.item_id,
        productId: item.product_id,
        name: item.product?.name || "Unknown product",
        images: item.product?.images,
        isGift: !!item.is_gift,
        unitPrice: Number(item.unit_price) || 0,
        quantity: item.quantity,
        originalQuantity: item.quantity,
        removed: false,
    }));

const lineAmount = (l: Line) => lineTotal({ quantity: l.quantity, unit_price: l.unitPrice, is_gift: l.isGift });

/** "Cat Collar" or "Cat Collar (gift)" — a product can be on an order both ways. */
const lineName = (l: Line) => (l.isGift ? `${l.name} (gift)` : l.name);

/**
 * The drawer's item editor: stage quantity changes, removals and new
 * products, then review once and save. Saving goes through the existing
 * stock-aware item endpoints in a safe order — adds, then quantity changes,
 * then removals (so an order never passes through zero items) — and always
 * re-reads the order afterwards so what's shown is what the server has.
 */
export const OrderItemsEditor: React.FC<OrderItemsEditorProps> = ({
    order,
    paid,
    onDone,
    onDirtyChange,
    onOrderChanged,
    notify,
}) => {
    const [lines, setLines] = useState<Line[]>(() => linesFrom(order));
    const [pickerKey, setPickerKey] = useState(0);
    /** What the add box adds: a paid line, or a free gift line. */
    const [addAs, setAddAs] = useState<"product" | "gift">("product");
    const [hint, setHint] = useState("");
    const [checking, setChecking] = useState(false);
    const [review, setReview] = useState<Review | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [allowNegativeStock, setAllowNegativeStock] = useState(false);
    const [outcome, setOutcome] = useState<string | null>(null);
    const { saving, setSaving, error, setError, refresh } = useQuickEditSave(order.order_id, onOrderChanged, notify);

    const added = lines.filter((l) => !l.itemId);
    const changed = lines.filter((l) => l.itemId && !l.removed && l.quantity !== l.originalQuantity);
    const removed = lines.filter((l) => l.itemId && l.removed);
    const remaining = lines.filter((l) => !l.removed);
    const dirty = added.length + changed.length + removed.length > 0;
    const invalid = remaining.some((l) => !Number.isInteger(l.quantity) || l.quantity < 1);

    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    const oldTotal = round2(Number(order.total_amount));
    const newTotal = orderTotalFor(round2(remaining.reduce((sum, l) => sum + lineAmount(l), 0)), order);

    const update = (key: string, patch: Partial<Line>) => {
        setHint("");
        setLines((prev) =>
            prev.map((l) => {
                if (l.key !== key) return l;
                const next = { ...l, ...patch };
                if (next.product && next.quantity > 0) next.unitPrice = round2(unitPriceFor(next.product, next.quantity));
                return next;
            }),
        );
    };

    const removeLine = (line: Line) => {
        setHint("");
        if (line.itemId) update(line.key, { removed: true });
        else setLines((prev) => prev.filter((l) => l.key !== line.key));
    };

    const addProduct = (product: Product | null) => {
        if (!product) return;
        const isGift = addAs === "gift";
        setPickerKey((k) => k + 1); // clear the search box
        setAddAs("product"); // back to paid, so nothing is added free by accident
        const existing = lines.find((l) => l.productId === product.product_id && l.isGift === isGift);
        if (existing) {
            if (existing.removed) {
                update(existing.key, { removed: false });
                setHint(`${lineName(existing)} is back on the order.`);
            } else {
                update(existing.key, { quantity: Math.min(MAX_QTY, existing.quantity + 1) });
                setHint(`${lineName(existing)} is already on the order — quantity increased.`);
            }
            return;
        }
        setHint("");
        setLines((prev) => [
            ...prev,
            {
                key: `new-${isGift ? "gift-" : ""}${product.product_id}`,
                productId: product.product_id,
                name: product.name,
                images: product.images,
                isGift,
                unitPrice: isGift ? 0 : round2(unitPriceFor(product, 1)),
                quantity: 1,
                product: isGift ? undefined : product,
                removed: false,
            },
        ]);
    };

    const cancel = () => {
        if (dirty && !window.confirm("Discard your item changes?")) return;
        onDone();
    };

    const openReview = async () => {
        setChecking(true);
        const nameOf = (productId: string) => lines.find((l) => l.productId === productId)?.name ?? "A product";

        // Stock for what's being added or increased — drafts hold no stock.
        let stockIssues: string[] = [];
        if (order.status !== "DRAFT") {
            const needed = new Map<string, number>();
            for (const l of added) needed.set(l.productId, (needed.get(l.productId) ?? 0) + l.quantity);
            for (const l of changed) {
                const extra = l.quantity - (l.originalQuantity ?? 0);
                if (extra > 0) needed.set(l.productId, (needed.get(l.productId) ?? 0) + extra);
            }
            if (needed.size > 0) {
                try {
                    const res = await stockService.checkMultipleStockAvailability(
                        Array.from(needed, ([product_id, quantity]) => ({ product_id, quantity })),
                    );
                    stockIssues = res.items
                        .filter((i) => !i.available)
                        .map((i) => i.reason || `${nameOf(i.product_id)}: not enough stock`);
                } catch {
                    // The server checks stock again when saving.
                }
            }
        }

        const rows: ChangeRow[] = [
            ...added.map((l) => ({
                label: `Add ${lineName(l)}`,
                to: `×${l.quantity} · ${l.isGift ? "Free" : formatCurrencyBDT(lineAmount(l))}`,
                tone: "add" as const,
            })),
            ...changed.map((l) => ({ label: lineName(l), from: `×${l.originalQuantity}`, to: `×${l.quantity}` })),
            ...removed.map((l) => ({
                label: `Remove ${lineName(l)}`,
                to: `×${l.originalQuantity}`,
                tone: "remove" as const,
            })),
        ];

        const steps: Step[] = [
            ...added.map((l) => ({
                label: `Add ${lineName(l)} ×${l.quantity}`,
                run: (allowNegative: boolean) =>
                    orderService.addOrderItem(order.order_id, {
                        product_id: l.productId,
                        quantity: l.quantity,
                        unit_price: l.isGift ? 0 : l.unitPrice,
                        total_price: lineAmount(l),
                        is_gift: l.isGift,
                        allow_negative_stock: allowNegative,
                    }),
            })),
            ...changed.map((l) => ({
                label: `${lineName(l)} ×${l.originalQuantity} → ×${l.quantity}`,
                run: (allowNegative: boolean) =>
                    orderService.updateOrderItem(l.itemId!, { quantity: l.quantity, allow_negative_stock: allowNegative }),
            })),
            ...removed.map((l) => ({
                label: `Remove ${lineName(l)}`,
                run: () => orderService.deleteOrderItem(l.itemId!),
            })),
        ];

        const warnings: string[] = [];
        const cod = codWarning(order, newTotal);
        if (cod) warnings.push(cod);
        if (order.status === "DELIVERED") warnings.push("This order is delivered — stock is adjusted as soon as you save.");

        setReview({ rows, impact: { oldTotal, newTotal, paid }, warnings, stockIssues, steps });
        setAllowNegativeStock(false);
        setError("");
        setChecking(false);
        setConfirmOpen(true);
    };

    const confirm = async () => {
        if (!review) return;
        setSaving(true);
        setError("");
        const saved: string[] = [];
        let failure: { label: string; reason: string } | null = null;
        for (const step of review.steps) {
            try {
                await step.run(allowNegativeStock);
                saved.push(step.label);
            } catch (err) {
                failure = { label: step.label, reason: errorText(err) };
                break;
            }
        }
        // Always re-read: the screen must show what the server now has.
        const fresh = await refresh();
        setSaving(false);

        if (!failure) {
            setConfirmOpen(false);
            notify("Items updated");
            onDone();
            return;
        }
        if (saved.length === 0) {
            // Nothing changed — keep the staged edits so they can be fixed.
            setError(failure.reason);
            return;
        }
        // Partly saved: start again from the order as it is now.
        setConfirmOpen(false);
        if (fresh) setLines(linesFrom(fresh));
        const skipped = review.steps.slice(saved.length + 1).map((s) => s.label);
        setOutcome(
            `Saved: ${saved.join(", ")}. Not saved: ${failure.label} — ${failure.reason}` +
                (skipped.length ? `. Skipped: ${skipped.join(", ")}.` : ""),
        );
    };

    const statusNote =
        order.status === "DRAFT"
            ? "Draft — no stock is reserved until the order is finalized."
            : order.status === "DELIVERED"
              ? "Delivered — stock is adjusted as soon as you save."
              : null;

    return (
        <Paper variant="outlined" sx={{ mb: 3 }}>
            {outcome && (
                <Alert severity="warning" onClose={() => setOutcome(null)} sx={{ borderRadius: 0 }}>
                    {outcome}
                </Alert>
            )}
            {statusNote && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, pt: 1 }}>
                    {statusNote}
                </Typography>
            )}

            <Stack divider={<Divider flexItem />}>
                {lines.map((line) => {
                    const onlyOneLeft = !line.removed && remaining.length === 1;
                    const qtyInvalid = !line.removed && (!Number.isInteger(line.quantity) || line.quantity < 1);
                    return (
                        <Box
                            key={line.key}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: 1.5,
                                py: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                                bgcolor: (t) =>
                                    line.removed
                                        ? t.palette.action.hover
                                        : line.isGift
                                          ? alpha(t.palette.secondary.main, 0.06)
                                          : !line.itemId
                                            ? alpha(t.palette.success.main, 0.06)
                                            : "transparent",
                            }}
                        >
                            <ProductThumbnail images={line.images} alt={line.name} />
                            <Box sx={{ flex: "1 1 150px", minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                        textDecoration: line.removed ? "line-through" : undefined,
                                        color: line.removed ? "text.disabled" : undefined,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {line.name}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                    {line.isGift && (
                                        <Chip
                                            icon={<CardGiftcard fontSize="small" />}
                                            label="Gift"
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ height: 20, "& .MuiChip-label": { px: 0.75 } }}
                                        />
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {line.isGift ? "Free" : `${formatCurrencyBDT(line.unitPrice)} each`}
                                        {!line.itemId && " · new"}
                                        {line.removed && " · will be removed"}
                                    </Typography>
                                </Box>
                                <ComboPriceHint product={line.product} quantity={line.quantity} unitPrice={line.unitPrice} />
                            </Box>

                            {line.removed ? (
                                <Button size="small" startIcon={<Undo />} onClick={() => update(line.key, { removed: false })}>
                                    Undo
                                </Button>
                            ) : (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        aria-label={`Decrease ${line.name}`}
                                        disabled={line.quantity <= 1}
                                        onClick={() => update(line.key, { quantity: Math.max(1, line.quantity - 1) })}
                                    >
                                        <Remove fontSize="small" />
                                    </IconButton>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={line.quantity === 0 ? "" : line.quantity}
                                        error={qtyInvalid}
                                        onChange={(e) => {
                                            const n = parseInt(e.target.value, 10);
                                            update(line.key, { quantity: Number.isNaN(n) ? 0 : Math.min(MAX_QTY, Math.max(0, n)) });
                                        }}
                                        inputProps={{
                                            min: 1,
                                            max: MAX_QTY,
                                            inputMode: "numeric",
                                            "aria-label": `Quantity of ${line.name}`,
                                            style: { textAlign: "center", width: 44, padding: "6px 4px" },
                                        }}
                                        // The −/+ buttons replace the browser's tiny spinner.
                                        sx={{
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                            },
                                            "& input[type=number]": { MozAppearance: "textfield" },
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        aria-label={`Increase ${line.name}`}
                                        disabled={line.quantity >= MAX_QTY}
                                        onClick={() => update(line.key, { quantity: Math.min(MAX_QTY, line.quantity + 1) })}
                                    >
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}

                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ width: 96, textAlign: "right", fontVariantNumeric: "tabular-nums", ml: "auto" }}
                            >
                                {line.removed ? "—" : line.isGift ? "Free" : formatCurrencyBDT(lineAmount(line))}
                            </Typography>

                            {!line.removed && (
                                <Tooltip title={onlyOneLeft ? LAST_ITEM : "Remove from order"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            aria-label={`Remove ${line.name}`}
                                            disabled={onlyOneLeft}
                                            onClick={() => removeLine(line)}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    );
                })}
            </Stack>

            <Box sx={{ px: 1.5, py: 1.5, borderTop: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={addAs}
                        onChange={(_, value) => value && setAddAs(value)}
                        aria-label="Add as"
                        sx={{ mt: 0.5, flexShrink: 0 }}
                    >
                        <ToggleButton value="product" aria-label="Add as a paid product" sx={{ px: 1.5 }}>
                            Product
                        </ToggleButton>
                        <ToggleButton
                            value="gift"
                            color="secondary"
                            aria-label="Add as a free gift"
                            sx={{ px: 1.5, gap: 0.5 }}
                        >
                            <CardGiftcard fontSize="small" />
                            Gift
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        <ProductSearchAutocomplete
                            key={pickerKey}
                            value={null}
                            onChange={addProduct}
                            label={addAs === "gift" ? "Add a free gift" : "Add a product"}
                            secondaryLine="stock"
                        />
                    </Box>
                </Box>
                {hint && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        {hint}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                    borderTop: 1,
                    borderColor: "divider",
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        Order total{" "}
                        <strong>
                            {dirty && newTotal !== oldTotal ? `${formatCurrencyBDT(oldTotal)} → ` : ""}
                            {formatCurrencyBDT(dirty ? newTotal : oldTotal)}
                        </strong>
                    </Typography>
                    <Typography variant="caption" color={remaining.length === 0 ? "error" : "text.secondary"}>
                        {remaining.length === 0
                            ? LAST_ITEM
                            : `Paid ${formatCurrencyBDT(paid)} · ${balanceText(dirty ? newTotal : oldTotal, paid)}`}
                    </Typography>
                </Box>
                <Button color="inherit" onClick={cancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!dirty || invalid || remaining.length === 0 || checking || saving}
                    onClick={openReview}
                >
                    {checking ? "Checking stock…" : "Review & save"}
                </Button>
            </Box>

            <ConfirmChangeDialog
                open={confirmOpen}
                title="Save item changes?"
                rows={review?.rows ?? []}
                impact={review?.impact}
                warnings={review?.warnings}
                stockIssues={review?.stockIssues}
                allowNegativeStock={allowNegativeStock}
                onAllowNegativeStockChange={setAllowNegativeStock}
                error={error}
                saving={saving}
                confirmLabel="Save changes"
                onConfirm={confirm}
                onCancel={() => setConfirmOpen(false)}
            />
        </Paper>
    );
};
